import { VxSwal } from '@voxie/frontend-components';
import { createGlobalState } from '@vueuse/core';
import { computed, reactive, ref } from 'vue';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { FEATURES } from '~/provides/features';
import { actionsClient, messagesClient, scheduledMessagesClient, threadsClient } from '~/services';
import { getClientTz } from '~/utils/date';
import dayjs from '~/utils/dayjs';
import { isAuthErrorEcho } from '~/utils/helpers';
import { isSameThread } from '~/components/message-hub/messageHubUtil';
import { catchRequestError } from '~/utils/request';
import { useCursorPagination } from './useCursorPagination';
import { useToasted } from './useToasted';
import axios from 'axios';
import { contactName } from '~/components/filters';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import Bus from '~/globals/eventBus';

export const useConversation = createGlobalState(() => {
    const toasted = useToasted();
    const { currentInbox } = useMessageHub();

    const { getCurrentGroupId, getContext } = useCurrentGroup();

    // Only use the contact variable from here to get the active thread. Do not use it's data elsewhere, always rely on the active thread.
    // The contact might not exist when in a group because it's no longer part of the group, but the thread still exists
    const {
        contact,
        contactNotFound,
        mount: mountContact,
        getSubscriptions,
        prefill: prefillContact,
    } = useContactDetails();

    const activeThread = ref();

    const { list, fetchParams, loading: loadingMessages, isLast, fetchRecords } = useCursorPagination();

    const mounting = ref(true);
    const changingAssignee = ref(false);
    const togglingReadUnread = ref(false);
    const changingThreadStatus = ref(false);

    // Track threads that should be reselected after undo operations
    const pendingUndoReselections = ref(new Map());

    const sendingMessage = ref(false);
    const message = reactive({
        body: '',
        media_url: null,
        media_content_type: null,
        send_at: null,
    });

    const resetMessageData = () => {
        message.body = '';
        message.media_url = null;
        message.media_content_type = null;
        message.send_at = null;
    };

    const validateMessage = (messagePayload) => {
        if (!messagePayload.body || messagePayload.body.trim() === '') {
            VxSwal.fire({
                title: 'Whoops!',
                text: 'Message body is required.',
                icon: 'error',
            });
            return false;
        }

        if (messagePayload.send_at) {
            if (
                messagePayload.send_at !== null &&
                dayjs().tz(getClientTz()).utc().isSameOrAfter(dayjs.utc(messagePayload.send_at).tz(getClientTz()))
            ) {
                VxSwal.fire({
                    title: 'Whoops!',
                    text: 'The send at field cannot be set in the past.',
                    icon: 'error',
                });
                return false;
            }
        }

        return true;
    };

    const deliverMessage = async (messagePayload) => {
        if (sendingMessage.value) {
            return;
        }

        if (!validateMessage(messagePayload)) {
            return;
        }

        sendingMessage.value = true;

        const payload = {
            contact_id: activeThread.value.contact_id,
            ...messagePayload,
        };

        try {
            if (messagePayload.send_at) {
                await scheduledMessagesClient.createScheduledMessage(
                    { teamId: activeThread.value.team_id, groupId: activeThread.value.group_id },
                    activeThread.value.contact_id,
                    payload
                );
            } else {
                if (!FEATURES.simple_message_hub && activeThread.value.status === 'closed') {
                    changeActiveThreadStatus(activeThread.value, 'open', {
                        showToast: false,
                        showUndo: false,
                    });

                    if (activeThread.value.assignee_id !== Spark.state.user.id) {
                        assignThreadRequest(activeThread.value, Spark.state.user.id);
                        activeThread.value.assignee_id = Spark.state.user.id;
                    }
                }

                if (activeThread.value.group_id) {
                    await messagesClient.groupMessageCreate(
                        activeThread.value.team_id,
                        activeThread.value.group_id,
                        payload
                    );
                } else {
                    await messagesClient.createV3(activeThread.value.team_id, payload);
                }

                const updatedThread = {
                    ...activeThread.value,
                    last_message_content:
                        !messagePayload.body && messagePayload.media_url ? 'Attachment' : messagePayload.body,
                    last_message_direction: 'outbound',
                    last_message_at: new Date().toISOString(),
                };

                Bus.$emit('threadUpdated', { thread: updatedThread });
            }

            actionsClient.send('message_sent');
            if (messagePayload.send_at) {
                toasted.success('Your message has been scheduled!');
            }
            resetMessageData();
        } finally {
            sendingMessage.value = false;
        }
    };

    const actionInProgress = computed(() => {
        return Boolean(
            changingAssignee.value || togglingReadUnread.value || changingThreadStatus.value || sendingMessage.value
        );
    });

    const defaultFilters = () => ({
        per_page: 15,
        cursor: null,
        sort_dir: 'desc',
    });

    const shouldShowNewLine = (targetMessage) => {
        return Boolean(
            !FEATURES.simple_message_hub &&
                activeThread.value &&
                lastUnreadMessage.value &&
                targetMessage.id === lastUnreadMessage.value.id &&
                (targetMessage.body || targetMessage.media_url)
        );
    };

    const messageGroups = computed(() => {
        const messagesToDay = ([day, group]) => {
            const messages = group.sort((mA, mB) => mA.id - mB.id);
            return {
                lastUnreadInboundMessage: shouldShowNewLine(messages[0]),
                fullDate: day,
                prettyDate: dayjs(day).tz(getClientTz()).format('MMM Do, YYYY'),
                messages,
            };
        };

        const groups = list.value.reduce((groups, targetMessage) => {
            const groupKey = dayjs.utc(targetMessage.created_at).tz(getClientTz()).startOf('day').toString();
            if (!groups[groupKey]) {
                groups[groupKey] = [];
            }

            groups[groupKey].push(targetMessage);

            return groups;
        }, {});

        return Object.entries(groups)
            .map(messagesToDay)
            .sort((gA, gB) => {
                return new Date(gB.fullDate).getTime() - new Date(gA.fullDate).getTime();
            });
    });

    const lastUnreadMessage = computed(() => {
        if (!activeThread.value) {
            return;
        }

        const messages = list.value;
        const flteredMessages = messages
            .sort((mA, mB) => mB.id - mA.id)
            .filter((m) => {
                // All messages except user's one can be unread.
                return (
                    m.direction === 'inbound' ||
                    (m.direction === 'outbound' &&
                        (m.origin_meta.type !== 'Message Hub' || m.origin_meta.id !== Spark.state.user.id))
                );
            });

        const unreadMessages = flteredMessages.slice(0, activeThread.value.unread);

        return unreadMessages[unreadMessages.length - 1];
    });

    const unassignThread = async (thread, options = {}) => {
        options.showToast = options.showToast ?? true;
        options.showUndo = options.showUndo ?? true;

        if (changingAssignee.value) {
            return;
        }

        const previousAssigneeId = thread.assignee_id;

        changingAssignee.value = true;

        const context = { teamId: thread.team_id, groupId: thread.group_id };
        const request = threadsClient.unclaim(context, thread.contact_id, thread.group_id);

        try {
            await request;

            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                activeThread.value.assignee_id = null;
            }

            Bus.$emit('threadUpdated', { thread: { ...thread, assignee_id: null } });

            if (options.showToast) {
                addPendingUndoReselection(thread.contact_id, currentInbox.value);

                const toastConfig = {
                    title: 'The thread was unassigned successfully.',
                    type: 'success',
                };

                if (options.showUndo) {
                    toastConfig.action = {
                        label: 'Undo',
                        onClick: async () => {
                            toasted.dismiss(toastId);
                            await assignThread({ ...thread, assignee_id: null }, previousAssigneeId, {
                                showToast: true,
                                showUndo: false,
                            });
                        },
                    };
                }

                const toastId = toasted.show(toastConfig);
            }
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            changingAssignee.value = false;
        }
    };

    const assignThreadRequest = (thread, assigneeId) => {
        const assignToMySelf = assigneeId === Spark.state.user.id;
        const context = { teamId: thread.team_id, groupId: thread.group_id };

        return assignToMySelf
            ? threadsClient.assignToSelf(context, thread.contact_id, thread.group_id)
            : threadsClient.assignTo(context, thread.contact_id, { assignee_id: assigneeId }, thread.group_id);
    };

    const assignThread = async (thread, assigneeId, options = {}) => {
        options.showToast = options.showToast ?? true;
        options.showUndo = options.showUndo ?? true;

        if (changingAssignee.value) {
            return;
        }

        const previousAssigneeId = thread.assignee_id;
        const previousStatus = thread.status;
        const threadSnapshot = { ...thread };

        changingAssignee.value = true;

        try {
            if (previousStatus !== 'open') {
                await changeActiveThreadStatus({ ...thread }, 'open', {
                    showToast: false,
                });
            }

            await assignThreadRequest(thread, assigneeId);

            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                activeThread.value.assignee_id = assigneeId;
            }

            Bus.$emit('threadUpdated', { thread: { ...thread, assignee_id: assigneeId, status: 'open' } });

            if (options.showToast) {
                addPendingUndoReselection(thread.contact_id, currentInbox.value);

                const toastConfig = {
                    title: 'The thread was assigned successfully.',
                    type: 'success',
                };

                if (options.showUndo) {
                    toastConfig.action = {
                        label: 'Undo',
                        onClick: async () => {
                            toasted.dismiss(toastId);
                            await undoAssignThread(
                                {
                                    ...threadSnapshot,
                                    status: previousStatus !== 'open' ? 'open' : previousStatus,
                                },
                                previousAssigneeId,
                                previousStatus
                            );
                        },
                    };
                }

                const toastId = toasted.show(toastConfig);
            }
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            changingAssignee.value = false;
        }
    };

    const undoAssignThread = async (threadSnapshot, previousAssigneeId, previousStatus) => {
        try {
            if (previousAssigneeId === null) {
                await unassignThread(threadSnapshot, { showToast: true, showUndo: false });
            } else {
                // Was assigned to someone, restore that assignment
                await assignThread(threadSnapshot, previousAssigneeId, { showToast: true, showUndo: false });
            }

            // Restore previous status if it was changed by the assignment
            if (previousStatus !== 'open' && threadSnapshot.status !== previousStatus) {
                await changeActiveThreadStatus(threadSnapshot, previousStatus, { showToast: true, showUndo: false });
            }
        } catch (e) {
            // Remove from pending reselection if undo fails
            removePendingUndoReselection(threadSnapshot.contact_id);
            toasted.error(catchRequestError(e));
        }
    };

    const claimThread = async (thread, options = {}) => {
        return assignThread(thread, Spark.state.user.id, options);
    };

    const markAsUnread = async (thread, options = {}) => {
        options.showToast = options.showToast ?? true;
        options.showUndo = options.showUndo ?? true;

        if (togglingReadUnread.value) {
            return;
        }

        if (FEATURES.simple_message_hub) {
            togglingReadUnread.value = true;
            await changeActiveThreadStatus(thread, 'open');
            togglingReadUnread.value = false;
            return;
        }

        togglingReadUnread.value = true;
        try {
            const context = { teamId: thread.team_id, groupId: thread.group_id };
            const result = await threadsClient.checkpointDelete(context, thread.contact_id, thread.group_id);

            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                activeThread.value.unread = 1;
                activeThread.value.message_count = result.data?.message_count;
            }

            if (options.showToast) {
                const toastConfig = {
                    title: 'Conversation marked as unread.',
                    type: 'success',
                };

                if (options.showUndo) {
                    toastConfig.action = {
                        label: 'Undo',
                        onClick: async () => {
                            toasted.dismiss(toastId);
                            await markAsRead(thread, { showToast: true, showUndo: false });
                        },
                    };
                }

                const toastId = toasted.show(toastConfig);
            }
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            togglingReadUnread.value = false;
        }
    };

    const markAsRead = async (thread, options = {}) => {
        options.showToast = options.showToast ?? true;
        options.showUndo = options.showUndo ?? true;

        if (togglingReadUnread.value) {
            return;
        }

        if (FEATURES.simple_message_hub) {
            togglingReadUnread.value = true;
            await changeActiveThreadStatus(thread, 'closed', options);
            togglingReadUnread.value = false;
            return;
        }

        if (!thread.unread) {
            return;
        }

        togglingReadUnread.value = true;

        try {
            const context = { teamId: thread.team_id, groupId: thread.group_id };
            const result = await threadsClient.checkpointSave(context, thread.contact_id, thread.group_id);

            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                activeThread.value.unread = 0;
                activeThread.value.message_count = result.data?.message_count;
            }

            if (options.showToast) {
                const toastConfig = {
                    title: 'Conversation marked as read.',
                    type: 'success',
                };

                if (options.showUndo) {
                    toastConfig.action = {
                        label: 'Undo',
                        onClick: async () => {
                            toasted.dismiss(toastId);
                            await markAsUnread(thread, { showToast: true, showUndo: false });
                        },
                    };
                }

                const toastId = toasted.show(toastConfig);
            }
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            togglingReadUnread.value = false;
        }
    };

    let loadMessagesAbort;
    const loadMessages = async () => {
        loadMessagesAbort?.abort();
        loadMessagesAbort = new AbortController();

        const getLastMessage = list.value.length === 0;
        // at some point activeThread becomes false after listMessages/groupListMessages request,
        // so we need to save threads data
        const currentThread = activeThread.value;

        const messagesData = await fetchRecords(
            !activeThread.value.group_id
                ? messagesClient.listMessages(
                      activeThread.value.team_id,
                      activeThread.value.contact_id,
                      {
                          ...fetchParams.value,
                      },
                      {
                          signal: loadMessagesAbort.signal,
                      }
                  )
                : messagesClient.groupListMessages(
                      activeThread.value.team_id,
                      activeThread.value.group_id,
                      activeThread.value.contact_id,
                      {
                          ...fetchParams.value,
                      },
                      {
                          signal: loadMessagesAbort.signal,
                      }
                  )
        );

        if (getLastMessage) {
            const latestMessage = messagesData?.[0];

            if (latestMessage && currentThread.last_message_id !== latestMessage.id) {
                const updatedThread = {
                    ...currentThread,
                    last_message_content:
                        latestMessage.type === 'mms' && '' === latestMessage.body ? 'Attachment' : latestMessage.body,
                    last_message_direction: latestMessage.direction,
                    last_message_at: latestMessage.created_at,
                    last_message_id: latestMessage.id,
                };

                Bus.$emit('threadUpdated', { thread: updatedThread });
            }
        }

        return messagesData;
    };

    const unmount = () => {
        activeThread.value = false;
        list.value = [];

        fetchParams.value = defaultFilters();

        changingAssignee.value = false;
        togglingReadUnread.value = false;
        changingThreadStatus.value = false;

        resetMessageData();
    };

    let getThreadAbort;
    const getActiveThread = async (groupId) => {
        if (!contact.value.id) {
            return;
        }

        getThreadAbort?.abort();
        getThreadAbort = new AbortController();

        const context = getContext();
        const threads =
            (
                await threadsClient.getThreads(
                    context,
                    { contact_id: contact.value.id },
                    { signal: getThreadAbort.signal }
                )
            )?.data?.data || [];

        const matchingThread = groupId ? threads.find((t) => t.group_id === groupId) : threads[0];

        // If no existing thread is found (e.g., for a new contact), return a default thread object using the current team, group, and contact details.
        return (
            matchingThread || {
                team_id: contact.value.team_id,
                group_id: groupId || getCurrentGroupId(),
                contact_id: contact.value.id,
                contact_phone: contact.value.phone,
                contact_name: contactName(contact.value),
                status: FEATURES.simple_message_hub ? 'closed' : 'open',
            }
        );
    };

    const mount = async (existingThread, groupId) => {
        if (!contact.value?.id && !existingThread) {
            mounting.value = false;
            return;
        }

        // this fills the contact data before the conversation right from this composable
        if (existingThread && contact.value.id !== existingThread.contact_id) {
            prefillContact(existingThread.contact_id, existingThread.group_id, {
                id: existingThread.contact_id,
                team_id: existingThread.team_id,
                group_id: existingThread.group_id,
                first_name: existingThread.contact_name,
                last_name: undefined,
                phone: existingThread.contact_phone,
            });
        }

        unmount();

        mounting.value = true;

        try {
            activeThread.value = existingThread ? existingThread : await getActiveThread(groupId);

            if (!activeThread.value) {
                return;
            }

            if (contact.value.id !== activeThread.value.contact_id) {
                return;
            }

            loadMessages();

            // subscribe only if successfull
            subscribeEvents();
            markAsRead(activeThread.value, { showToast: false });
            getSubscriptions();
        } catch (e) {
            if (!axios.isCancel(e)) {
                toasted.error(catchRequestError(e));
            }
        } finally {
            mounting.value = false;
        }
    };

    const changeThreadStatusRequest = (thread, status) => {
        const context = { teamId: thread.team_id, groupId: thread.group_id };
        return threadsClient.setState(context, thread.contact_id, status, thread.group_id);
    };

    const changeActiveThreadStatus = async (thread, status, options = {}) => {
        options.showToast = options.showToast ?? true;
        options.showUndo = options.showUndo ?? true;
        if (thread?.status === status) {
            return;
        }

        if (changingThreadStatus.value) {
            return;
        }

        const previousStatus = thread.status;
        const previousAssigneeId = thread.assignee_id;
        const threadSnapshot = { ...thread };

        changingThreadStatus.value = true;

        try {
            await changeThreadStatusRequest(thread, status);

            if (activeThread.value && activeThread.value.contact_id === thread.contact_id) {
                activeThread.value.status = status;
            }

            Bus.$emit('threadUpdated', { thread: { ...thread, status } });

            if (options.showToast) {
                const toastTitle = FEATURES.simple_message_hub
                    ? status === 'open'
                        ? 'Conversation marked as unread.'
                        : 'Conversation marked as read.'
                    : status === 'open'
                    ? 'Conversation status changed to open.'
                    : 'Conversation status changed to closed.';

                const toastConfig = {
                    title: toastTitle,
                    type: 'success',
                };

                if (options.showUndo) {
                    toastConfig.action = {
                        label: 'Undo',
                        onClick: async () => {
                            toasted.dismiss(toastId);
                            await undoStatusChange(
                                { ...threadSnapshot, status },
                                previousStatus,
                                previousAssigneeId,
                                thread.contact_id
                            );
                        },
                    };
                }

                const toastId = toasted.show(toastConfig);
            }
        } catch (e) {
            toasted.error(catchRequestError(e));
        } finally {
            changingThreadStatus.value = false;
        }
    };

    // Helper functions for undo reselection tracking
    const addPendingUndoReselection = (contactId, currentInbox) => {
        pendingUndoReselections.value.set(contactId, {
            inbox: currentInbox,
            timestamp: Date.now(),
        });

        setTimeout(() => {
            pendingUndoReselections.value.delete(contactId);
        }, 8000);
    };

    const isPendingUndoReselection = (contactId, currentInbox) => {
        const pending = pendingUndoReselections.value.get(contactId);
        return pending && pending.inbox === currentInbox;
    };

    const removePendingUndoReselection = (contactId) => {
        pendingUndoReselections.value.delete(contactId);
    };

    const undoStatusChange = async (threadSnapshot, previousStatus, previousAssigneeId, contactId) => {
        try {
            // Track this thread for reselection when it returns to the current inbox
            addPendingUndoReselection(contactId, currentInbox.value);

            await changeActiveThreadStatus(threadSnapshot, previousStatus, { showToast: true, showUndo: false });

            // If undoing close→open and thread had an assignee, restore the assignee
            if (previousStatus === 'open' && threadSnapshot.status === 'closed' && previousAssigneeId !== null) {
                await assignThread(threadSnapshot, previousAssigneeId, { showToast: true, showUndo: false });
            }
        } catch (e) {
            if (contactId) {
                removePendingUndoReselection(contactId);
            }
            toasted.error(catchRequestError(e));
        }
    };

    const closeWithSnippet = async ({ body, media_url, media_content_type }) => {
        try {
            await deliverMessage({ body, media_url, media_content_type });
            await changeActiveThreadStatus(activeThread.value, 'closed');
            actionsClient.send('snippet_used');
        } catch (e) {
            toasted.error(catchRequestError(e));
        }
    };

    const openWithSnippet = async ({ body, media_url, media_content_type }) => {
        try {
            await deliverMessage({ body, media_url, media_content_type });
            actionsClient.send('snippet_used');
        } catch (e) {
            toasted.error(catchRequestError(e));
        }
    };

    const syncActiveThread = (thread) => {
        if (!thread || !activeThread.value || !isSameThread(activeThread.value, thread)) {
            return false;
        }

        activeThread.value = {
            ...activeThread.value,
            ...thread,
        };

        return true;
    };

    const handleBusThreadUpdated = ({ thread }) => {
        syncActiveThread(thread);
    };

    let subscribedToEvents = false;
    const subscribeEvents = () => {
        if (subscribedToEvents) {
            return;
        }

        subscribedToEvents = true;
        const echoErrors = [];
        const { orgUnitId, groupId } = getContext();
        const channelName =
            !orgUnitId && !groupId
                ? `messages.${activeThread.value.team_id}`
                : orgUnitId
                ? `messages.${activeThread.value.team_id}.org_unit.${orgUnitId}`
                : `messages.${activeThread.value.team_id}.${groupId}`;
        Echo.private(channelName)
            .listen('MessageCreated', (payload) => {
                const message = payload?.message;
                if (!message) {
                    return;
                }

                if (message.contact_id !== activeThread.value?.contact_id) {
                    return;
                }

                if (
                    activeThread.value?.group_id &&
                    message.group_id &&
                    activeThread.value.group_id !== message.group_id
                ) {
                    return;
                }

                // Attempt contact refetch if contact is in "not found" state
                if (contactNotFound.value) {
                    mountContact(message.contact_id, getCurrentGroupId()).then(() => getSubscriptions());
                }

                list.value.push(message);

                // Read own message when it comes in.
                if (message.origin_meta.type === 'Message Hub' && message.origin_meta.id === Spark.state.user.id) {
                    markAsRead(activeThread.value, { showToast: false });
                } else if (undefined !== activeThread.value?.unread) {
                    // Mark rest of messages as unread.
                    activeThread.value.unread++;
                }
            })
            .listen('MessageUpdated', (payload) => {
                const message = payload?.message;
                if (!message) {
                    return;
                }

                if (message.contact_id !== activeThread.value?.contact_id) {
                    return;
                }

                if (
                    activeThread.value?.group_id &&
                    message.group_id &&
                    activeThread.value.group_id !== message.group_id
                ) {
                    return;
                }

                const messageIndex = list.value.findIndex((msg) => msg.id === message.id);
                if (messageIndex === -1) {
                    return;
                }
                const currentMessage = list.value[messageIndex];
                const currentStatus = currentMessage.status;
                const newStatus = message.status;

                const statusProgression = ['queued', 'processing', 'accepted', 'sent'];
                const terminalStatuses = ['failed', 'delivered', 'undelivered', 'expired'];

                const currentIndex = statusProgression.indexOf(currentStatus);
                const newIndex = statusProgression.indexOf(newStatus);

                // if current progression status isnt found do not update
                // and only updates forwards, never backwards
                // or if its a terminal status allow it to be updated
                if ((currentIndex !== -1 && newIndex > currentIndex) || terminalStatuses.includes(newStatus)) {
                    list.value[messageIndex] = {
                        ...currentMessage,
                        ...message,
                    };
                }
            })
            .listen('.ThreadUpdated', async ({ thread }) => {
                if (!syncActiveThread(thread)) {
                    return;
                }

                // Mark as read if assignee is current user.
                // For case when someone assigneed thread and then user tries to answer and assign thread to him self.
                if (activeThread.value?.assignee_id === Spark.state.user.id) {
                    markAsRead(activeThread.value, { showToast: false });
                }
            })
            .error?.((e) => {
                if (echoErrors.length <= 3 && !isAuthErrorEcho(e)) {
                    window?.Bugsnag?.notify(e instanceof Error ? e : new Error(JSON.stringify(e)));
                    echoErrors.push(e);
                }
            });

        Bus.$on('threadUpdated', handleBusThreadUpdated);
    };

    return {
        mount,
        unmount,
        mounting,
        subscribeEvents,
        markAsUnread,
        markAsRead,

        changingAssignee,
        unassignThread,
        assignThread,

        claimThread,
        changeActiveThreadStatus,
        closeWithSnippet,
        openWithSnippet,

        list,
        loadMessages,
        lastUnreadMessage,
        messageGroups,
        shouldShowNewLine,

        activeThread,
        loadingMessages,
        changingThreadStatus,

        togglingReadUnread,
        actionInProgress,
        isLast,
        message,
        resetMessageData,
        validateMessage,
        deliverMessage,
        sendingMessage,

        // Undo reselection helpers
        isPendingUndoReselection,
        removePendingUndoReselection,
    };
});
